/**
 * Shared server/client pagination math for list screens.
 * Use client page size + total record count as source of truth for total pages.
 */

export function isAllPageSize(pageSize) {
  if (pageSize === 'all' || pageSize === 'All') return true;
  const n = Number(pageSize);
  return n === 9999 || n === 1000; // legacy "all" limits in some screens
}

export const DEFAULT_PAGE_SIZE = 10;

export function parsePageSize(pageSize, fallback = DEFAULT_PAGE_SIZE) {
  if (isAllPageSize(pageSize)) return 'all';
  const n = parseInt(pageSize, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** totalRecords / pageSize → total pages (minimum 1 for empty lists). */
export function computeTotalPages(totalRecords, pageSize) {
  const total = Math.max(0, Number(totalRecords) || 0);
  if (isAllPageSize(pageSize)) return 1;
  const size = Math.max(1, Number(parsePageSize(pageSize)) || 1);
  if (total === 0) return 1;
  return Math.max(1, Math.ceil(total / size));
}

export function clampPage(page, totalPages) {
  const tp = Math.max(1, Number(totalPages) || 1);
  const p = Math.max(1, Number(page) || 1);
  return Math.min(p, tp);
}

/**
 * Merge API pagination with the client request (page + page size win).
 * Prevents stale responses from overwriting page size or inflating page count.
 */
export function mergeServerPagination(apiPagination = {}, { page, pageSize } = {}) {
  const total = Number(apiPagination.total ?? apiPagination.total_count ?? 0) || 0;
  const per_page = parsePageSize(pageSize ?? apiPagination.per_page);
  const numericPerPage = per_page === 'all' ? 9999 : per_page;
  const total_pages = computeTotalPages(total, per_page);
  const requestedPage = Number(page ?? apiPagination.current_page ?? 1) || 1;
  const current_page = clampPage(requestedPage, total_pages);

  return {
    ...apiPagination,
    total,
    per_page: numericPerPage,
    total_pages,
    current_page,
    has_next_page: current_page < total_pages,
    has_prev_page: current_page > 1
  };
}

/** Read page + page size from a Redux async thunk action.meta.arg. */
export function paginationFromThunkAction(action, apiPagination) {
  const args = action?.meta?.arg || {};
  const nested = args.params && typeof args.params === 'object' ? args.params : {};
  return mergeServerPagination(apiPagination, {
    page: nested.page ?? args.page ?? args.currentPage,
    pageSize:
      nested.limit ??
      nested.per_page ??
      args.limit ??
      args.per_page ??
      args.entriesPerPage ??
      args.pageSize
  });
}
